import {
  ARRANGEMENT_DEFAULT_WINDOW_DAYS,
  ARRANGEMENT_TYPE_KEYS,
  ARRANGEMENT_TYPES,
  type ArrangementSearchHintFilter,
} from '@pams/shared';
import { ARRANGEMENT_SEARCH_READS } from '@/lib/arrangementSearch';

/**
 * The arrangement list's own words. Plain sentences, in one place, so a test can assert
 * them — prose with nothing asserting it is prose that gets deleted in a tidy-up.
 */

/**
 * ⚠️ A LABEL FOR THE TAB, KEPT IN THE PAGE RATHER THAN IN `lib/labels.ts`. One reader is
 * not a reason to move a label up; the trigger is the day the arrangement DETAIL names the
 * kind too, which is `labels.ts`'s own stated promotion bar.
 */
export const ARRANGEMENT_KIND_LABEL = {
  STANDARD: 'Standard',
  ROUND_THE_CLOCK: 'Round the clock',
} as const;

export const GROUP_COPY = {
  /** ⚠️ ASSERTED BY A TEST. It is the whole of what grouping promises. */
  whatItDoes:
    'Grouping only changes how these orders are listed. It does not change their duties, who fills them, or their status.',
  ungroup: 'The orders stay exactly as they are. They just stop being listed together.',
  needsTwo: 'Choose at least two orders to group.',
  sameDateOnly: 'Every order in a group shares one order date.',
  alreadyGrouped: 'This order is already in a group. Ungroup it first, or add it from the group’s menu.',
  selected: (count: number): string => `${String(count)} selected`,
  partialOnPage: (shown: number, total: number): string => `${String(shown)} of ${String(total)} on this page`,
  partialHint: 'The rest of this group is on another page. Show more rows per page to see them together.',
  flatUnderSort: 'Groups are shown nested when the list is sorted by date.',
  groupThese: 'Group these',
  clear: 'Clear',
  modalTitle: (count: number): string => `Group ${String(count)} orders`,
  nameLabel: 'Group name',
} as const;

/**
 * ⚠️ [UX1 §8] WHY THE PRIMARY ACTION IS UNAVAILABLE, WHEN IT IS.
 *
 * It names the STATE OF THE DEPLOYMENT rather than the person, and it says what to do instead
 * — `DEACTIVATED_MESSAGE`'s rule. It deliberately does NOT name a pnpm script or a settings
 * page: a divisional nodal cannot act on either, and the second half is the reassurance that
 * matters. It is the server's own sentence, kept short enough for a tooltip.
 */
export const ARRANGEMENTS_COPY = {
  /**
   * [LIST-FILTERS] The untyped list's own title — a constant now because the filter dialog's intro
   * names it beside every typed list, and a second spelling of a page's name is a second name.
   */
  title: 'Police arrangements',
  mayNotRaise:
    'Arrangements are raised by the CP office on this deployment. You can still be allocated force, and fill it.',
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §2] THE UPLOADED-ORDER LIBRARY'S OWN WORDS.
 *
 *  ⚠️ `LIBRARY` IS NOT AN `ArrangementKind`, AND ITS LABEL IS HERE RATHER THAN IN
 *  `ARRANGEMENT_KIND_LABEL`. Putting it there is how a value the server's `z.enum` refuses ends
 *  up in `?kind=` and 422s the list on every keystroke.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const LIBRARY_COPY = {
  tab: 'Uploads',
  add: 'Upload a PA',
  modalTitle: 'Upload a PA order',
  title: 'Title',
  titlePlaceholder: 'What this order will be found by',
  orderDate: 'Order date',
  file: 'PDF',
  choose: 'Choose a PDF',
  save: 'Upload',
  view: 'View',
  remove: 'Remove',
  viewerTitle: 'Uploaded PA',
  download: 'Download',
  emptyTitle: 'No PAs have been uploaded.',
  emptyBody: 'A PA that was issued on paper can be filed here so it is on record beside the rest.',
  /** ⚠️ Stated where the operator meets it, with the number, from the same constant. */
  tooLarge: 'That file is larger than 5 MB.',
  /**
   * ⚠️ THE VIEWER RENDERS THE BYTES THE CLIENT ALREADY FETCHED, and a browser with its PDF
   * viewer disabled shows an EMPTY frame with no error event — there is nothing to detect. The
   * Download button is therefore unconditional rather than a fallback.
   */
  viewerNote: 'If the file does not appear, download it instead.',
  removeConfirm: 'Remove this file?',
  removeBody: 'The PDF is deleted. This cannot be undone.',
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5b §6] THE EMPTY TABLE'S THREE SENTENCES, AND THE DATE PICKERS' PLACEHOLDERS.
 *
 *  No search: the page's own copy, unchanged. A search the server says a FILTER hid: the filters,
 *  and one action. Otherwise: which tab, and what the search reads.
 *
 *  ⚠️ `noneTitle` SAYS "WITH THESE FILTERS", AND THAT IS NOT HEDGING. The server sends no hint in
 *  TWO cases the client cannot tell apart — the term is genuinely absent, or the probes ran out of
 *  their budget — so a sentence claiming no such order exists would be false in the second.
 *
 *  ⚠️ `noneBody` NAMES WHAT THE SEARCH READS — [amended C5c §7] the ONE sentence
 *  (`lib/arrangementSearch.ts`), which now leads with the paper PA number. It said "reference, title
 *  and places" and forbade "order number" because `arrangementListFilter` did not read `pa_number`;
 *  `arrangementSearchPredicate` does, so the prohibition is reversed rather than left standing.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const SEARCH_HINT_COPY = {
  emptyTitle: 'No arrangements yet.',
  emptyBody: 'Raise one from New arrangement, or start from a previous order.',
  hiddenTitle: (term: string): string => `Nothing matches “${term}” with these filters.`,
  hiddenBody: (filters: string): string => `Matching orders are hidden by ${filters}.`,
  reveal: 'Show matches',
  noneTitle: (kind: string, term: string): string => `No ${kind} arrangement matches “${term}” with these filters.`,
  noneBody: `Search looks at each order’s ${ARRANGEMENT_SEARCH_READS}.`,
  /**
   * ⚠️ THE PICKERS SAY WHICH WINDOW IS IN FORCE WHILE THEY ARE EMPTY — the inbox's idiom. With no
   * dates the SERVER applies today plus the default, from the same shared constant; with one bound
   * set, or the any-date sentinel, an empty picker really is any date.
   */
  fromDefault: 'Today',
  untilDefault: `In ${String(ARRANGEMENT_DEFAULT_WINDOW_DAYS)} days`,
  anyDate: 'Any date',
} as const;

/** Each hint filter as it reads inside `hiddenBody` — `satisfies`, so a seventh filter is a compile error. */
export const SEARCH_HINT_FILTER_LABEL = {
  dates: 'the dates',
  standingOrderId: 'the standing order',
  status: 'the status',
  show: 'the Show setting',
  /*
   * ⚠️ [amended LIST-FILTERS] "THE DIVISION FILTER", NOT "THE DIVISION". It is a session filter set from
   * "Adjust filters" now, holding one division or several, so "hidden by the division" was wrong in
   * number and pointed at nothing on screen; this names the control that set it. "Show matches" clears
   * it on both order lists, in every tab — the cost `listFilters.ts` states.
   */
  divisionOrgUnitId: 'the division filter',
  policyId: 'the policy',
} as const satisfies Record<ArrangementSearchHintFilter, string>;

/** "Central and North" — the platform's own list grammar, never a hand-rolled join. */
const CONJUNCTION = new Intl.ListFormat('en', { type: 'conjunction' });

/** Every typed list's name, as the sidebar says it — "Durga Puja PA". */
const TYPED_LIST_LABELS = ARRANGEMENT_TYPE_KEYS.map((type) => ARRANGEMENT_TYPES[type].label);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] "ADJUST FILTERS" — THE BUTTON, ITS DIALOG, AND THE EMPTY PAGE IT CAN CAUSE.
 *
 *  ⚠️ THE INTRO NAMES EVERY LIST THE FILTER REACHES, READ FROM `ARRANGEMENT_TYPES`. The filter is one
 *  per officer for both order lists, every tab, until sign-out, and an operator who narrowed Manage
 *  Arrangements must not be surprised to find Durga Puja PA narrowed too. A type registered next year
 *  joins the sentence with no edit here.
 *
 *  ⚠️ THE HELP LINE SAYS WHAT "DIVISION" MEANS, BECAUSE IT IS NARROWER THAN IT SOUNDS: the divisions
 *  an order asks FORCE of — the Divisions column. A draft with no force allocated yet has none, so it
 *  is hidden while the filter is on, and that is the row an operator just saved and comes back for.
 *
 *  ⚠️ A CHIP OR A SUMMARY NEVER PRINTS AN ID. While the divisions load a chosen id reads "Loading…"
 *  and the button's description "2 chosen"; only once the tree has ANSWERED does an id it cannot name
 *  read "no longer available" — `lib/selectOptions.ts`' `NOT_AVAILABLE` over an empty list would say
 *  that of every chosen division while it was merely still loading, which is false.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const LIST_FILTERS_COPY = {
  adjust: 'Adjust filters',
  applied: (count: number): string => `Filters applied · ${String(count)}`,
  modalTitle: 'Adjust filters',
  intro: `These filters apply to ${CONJUNCTION.format([
    ARRANGEMENTS_COPY.title,
    ...ARRANGEMENT_TYPE_KEYS.map((type) => ARRANGEMENT_TYPES[type].label),
  ])}, in every tab, until you sign out.`,
  division: 'Division',
  divisionPlaceholder: 'Any division',
  divisionHelp:
    'Shows only orders that ask force of one of these divisions. A draft with no force allocated yet is hidden while this is on.',
  territorial: 'Territorial divisions',
  wings: 'Wings',
  loading: 'Loading divisions…',
  chipLoading: 'Loading…',
  /** ⚠️ Not "No longer available": a tree that failed to load says nothing about whether a division exists. */
  chipFailed: 'Not loaded',
  none: 'No divisions in your scope',
  failed: 'The divisions could not be loaded.',
  /** The button's description, one line per active filter: "Division: Central and North". */
  summary: (names: readonly string[]): string => `Division: ${CONJUNCTION.format(names)}`,
  /** Between the description's lines — a native `title`, so never a newline. */
  summarySeparator: ' · ',
  /** While the names load — a count, never an id. */
  chosen: (count: number): string => `${String(count)} chosen`,
  /** Ids the caller's tree can no longer name, folded into one phrase rather than listed as hex. */
  unavailable: (count: number): string => `${String(count)} no longer available`,
  clear: 'Clear filters',
  cancel: 'Cancel',
  apply: 'Apply filters',
  emptyTitle: 'No arrangements match the filters applied.',
  typedEmptyTitle: (label: string): string => `No ${label} orders match the filters applied.`,
  emptyNote: 'Only orders that ask force of the divisions chosen are shown.',
  /*
   * [TYPE-FILTER] "Order type" — Manage Arrangements only; a typed list is its own type. The choices'
   * order and values are `ORDER_TYPE_CHOICES` (`listFilters.ts`); these are their words.
   */
  type: 'Order type',
  typeAll: 'All',
  typeNone: 'Regular PA only',
  typeOnly: (label: string): string => `${label} only`,
  typeHelp: `Applies to ${ARRANGEMENTS_COPY.title} only; ${CONJUNCTION.format(TYPED_LIST_LABELS)} always lists its own orders.`,
  typeSummary: (choice: string): string => `Order type: ${choice}`,
  typeNoneNote: `${CONJUNCTION.format(TYPED_LIST_LABELS)} orders are hidden.`,
  typeOnlyNote: (label: string): string => `Only ${label} orders are shown.`,
  /**
   * An empty search under a narrowing type, with no hint: the server's hint never names `type`, so the page says it
   * and offers the widening — promising no match, since nothing probed for one.
   */
  typeSet: (choice: string): string => `Order type is set to ${choice}.`,
  showAllTypes: 'Show all types',
  /**
   * [DP2026] "Booklet" — the Durga Puja PA list only: which template set made the orders. The year is the
   * type's own (`ARRANGEMENT_TYPES`), so next year's list reads its own year with no edit here.
   */
  booklet: 'Booklet',
  bookletAll: 'All booklets',
  bookletCurrent: (year: number): string => `${String(year)} booklet only`,
  bookletEarlier: 'Earlier booklets only',
  bookletHelp: (year: number): string =>
    `Which template booklet made the orders. Drafts from the ${String(year)} booklet are listed only here until they are published.`,
  bookletSummary: (choice: string): string => `Booklet: ${choice}`,
  bookletCurrentNote: (year: number): string => `Only orders made from the ${String(year)} booklet are shown.`,
  bookletEarlierNote: 'Only orders made from earlier booklets are shown.',
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B §5] THE TYPED TAB'S OWN WORDS — its empty page, its empty search, and its two actions.
 *
 *  ⚠️ ITS OWN, BECAUSE THE STANDARD SENTENCES ARE FALSE HERE. "Raise one from New arrangement" names
 *  a button the typed tab does not offer; a typed order is created from its templates.
 *
 *  ⚠️ [TYPED-BACK] `openRecord` IS THE TYPED LIST'S OWN ⋯ ENTRY ON A DRAFT — its door to the draft's
 *  record page, which its name (the builder, which comes straight back here) no longer reaches.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const TYPED_TAB_COPY = {
  templates: 'Templates',
  publish: 'Publish drafts',
  openRecord: 'Open the record',
  emptyTitle: (label: string): string => `No ${label} orders yet.`,
  emptyBody: 'They are created from the templates, and appear here as drafts.',
  noneTitle: (label: string, term: string): string => `No ${label} order matches “${term}” with these filters.`,
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B §6a] THE HOLDER'S DISCARD MODE.
 *
 *  ⚠️ "DISCARD", NEVER "DELETE" — [D4.5]'s rule for this act: each draft is archived exactly as one
 *  Discard would archive it, and can be restored for thirty days. The confirmation says both.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DISCARD_MODE_COPY = {
  start: 'Select drafts to discard',
  stop: 'Stop selecting drafts',
  region: 'Drafts selected to discard',
  selected: (count: number): string => `${String(count)} draft${count === 1 ? '' : 's'} selected`,
  draftsOnly: 'Only drafts on this page can be selected.',
  discard: (count: number): string => `Discard ${String(count)} draft${count === 1 ? '' : 's'}`,
  notADraft: 'Only a draft can be discarded here.',
  corrigendum: 'Part of a corrigendum family — discard it from its own page.',
  confirmTitle: (count: number): string => `Discard ${String(count)} draft${count === 1 ? '' : 's'}?`,
  confirmBody: 'Each draft is moved to the Archive, exactly as Discard does for one. Nobody is told — none was issued.',
  confirmRestorable: 'Any of them can be restored from the Archive for 30 days.',
  confirmOk: (count: number): string => `Discard ${String(count)}`,
  outcome: (discarded: number, refused: number): string =>
    refused === 0 ? `${String(discarded)} discarded.` : `${String(discarded)} discarded, ${String(refused)} refused:`,
  refusedNoReason: 'Refused.',
  dismiss: 'Dismiss',
} as const;
